import React from 'react';

/**
 * Button — the single source of truth for actionable controls across AgedCareWise.
 *
 * Why this exists: buttons were hand-rolled per screen, so the same action could be
 * blue/teal on one card, green on another, purple on a third. This primitive fixes the
 * visual language so hierarchy (not colour roulette) tells the user what matters.
 *
 * Hierarchy model — pick by ROLE, not by taste:
 *   primary    one per context. The main thing we want the user to do. Solid brand blue.
 *   brand      reserved for marquee / hero CTAs (page-level "Continue", landing). Signature gradient.
 *   secondary  supporting actions sitting next to a primary. White + slate border.
 *   ghost      low-emphasis / tertiary (Back, Cancel, inline toggles). Transparent.
 *   success    a semantic confirm where "go/keep" meaning genuinely helps (e.g. Select strategy).
 *   danger     destructive (Delete).
 *
 * Everything else (radius, weight, focus ring, transition, disabled, icon spacing, sizing)
 * is standardised here so it can never drift again.
 */

const VARIANTS = {
    primary:
        'bg-blue-600 text-white shadow-sm hover:bg-blue-700 focus-visible:ring-blue-500/50',
    brand:
        'bg-gradient-to-r from-blue-600 to-teal-500 text-white shadow-sm hover:from-blue-700 hover:to-teal-600 hover:shadow-md focus-visible:ring-blue-500/50',
    secondary:
        'bg-white text-slate-700 border border-slate-300 shadow-sm hover:bg-slate-50 hover:border-slate-400 focus-visible:ring-slate-400/50',
    ghost:
        'bg-transparent text-slate-600 hover:bg-slate-100 hover:text-slate-900 focus-visible:ring-slate-400/50',
    success:
        'bg-emerald-600 text-white shadow-sm hover:bg-emerald-700 focus-visible:ring-emerald-500/50',
    danger:
        'bg-white text-red-600 border border-red-200 shadow-sm hover:bg-red-50 hover:border-red-300 focus-visible:ring-red-500/40',
};

const SIZES = {
    sm: 'h-9 px-3.5 text-sm gap-1.5 rounded-lg',
    md: 'h-11 px-5 text-sm gap-2 rounded-lg',
    lg: 'h-12 px-7 text-base gap-2 rounded-xl',
};

const ICON_SIZE: Record<keyof typeof SIZES, number> = { sm: 16, md: 18, lg: 20 };

type IconComponent = React.ComponentType<{ size?: number; strokeWidth?: number; className?: string }>;

// Button is deliberately polymorphic (`as="a"`, `as={Link}`, etc.) — that makes fully precise
// per-element prop typing (e.g. `href` only valid when `as="a"`) a losing fight with TS generics
// for a handful of call sites. Pragmatic middle ground: base it on generic HTMLAttributes (covers
// onClick etc. for every element `as` could render) plus the small set of button/anchor-specific
// attributes actually used across call sites, and let the rest spread through untyped-but-honest.
interface ButtonProps extends Omit<React.HTMLAttributes<HTMLElement>, 'type'> {
    as?: React.ElementType;
    variant?: keyof typeof VARIANTS;
    size?: keyof typeof SIZES;
    icon?: IconComponent;
    iconRight?: IconComponent;
    fullWidth?: boolean;
    className?: string;
    children?: React.ReactNode;
    type?: React.ButtonHTMLAttributes<HTMLButtonElement>['type'];
    href?: string;
    disabled?: boolean;
    target?: string;
    rel?: string;
}

const Button = React.forwardRef<HTMLElement, ButtonProps>(function Button(
    {
        as = 'button',
        variant = 'primary',
        size = 'md',
        icon: Icon,
        iconRight: IconRight,
        fullWidth = false,
        className = '',
        children,
        type,
        ...props
    },
    ref,
) {
    const Component = as;
    const v = VARIANTS[variant] || VARIANTS.primary;
    const s = SIZES[size] || SIZES.md;
    const iconPx = ICON_SIZE[size] || ICON_SIZE.md;

    const classes = [
        'inline-flex items-center justify-center font-semibold tracking-tight whitespace-nowrap',
        'transition-all duration-200 select-none',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1',
        'disabled:opacity-50 disabled:pointer-events-none',
        v,
        s,
        fullWidth ? 'w-full' : '',
        className,
    ]
        .filter(Boolean)
        .join(' ');

    return (
        <Component
            ref={ref}
            className={classes}
            type={Component === 'button' ? type || 'button' : type}
            {...props}
        >
            {Icon && <Icon size={iconPx} strokeWidth={2.2} className="shrink-0" />}
            {children}
            {IconRight && <IconRight size={iconPx} strokeWidth={2.2} className="shrink-0" />}
        </Component>
    );
});

export default Button;
